// Centralized validation utilities
export const validationRules = {
  // Required field validation
  required: (fieldName) => (v) => !!v || `${fieldName} مطلوب`,
  
  // Email validation
  email: (v) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !v || pattern.test(v) || 'البريد الإلكتروني غير صحيح';
  },
  
  // Phone validation (Libyan format)
  phone: (v) => {
    const pattern = /^(091|092|093|094|095|021|061|051|041|024|025|043|047|057|063|041)\d{7}$/;
    return !v || pattern.test(v) || 'رقم الهاتف غير صحيح';
  },
  
  // National ID validation (Libyan format)
  nationalId: (v) => {
    const pattern = /^\d{12}$/;
    return !v || pattern.test(v) || 'الرقم الوطني يجب أن يكون 12 رقم';
  },
  
  // Employee number validation
  empNo: (v) => {
    const pattern = /^[A-Za-z0-9]+$/;
    return !v || pattern.test(v) || 'رقم الموظف يجب أن يحتوي على أرقام وحروف فقط';
  },
  
  // Username validation
  username: (v) => {
    const pattern = /^[a-zA-Z0-9_]{3,20}$/;
    return !v || pattern.test(v) || 'اسم المستخدم يجب أن يكون بين 3-20 حرف (أرقام وحروف و _ فقط)';
  },
  
  // Password validation
  password: (v) => {
    return !v || v.length >= 6 || 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
  },
  
  // Min length validation
  minLength: (min) => (v) => !v || v.length >= min || `يجب أن يكون ${min} أحرف على الأقل`,
  
  // Max length validation
  maxLength: (max) => (v) => !v || v.length <= max || `يجب أن يكون ${max} أحرف كحد أقصى`,
  
  // Number validation
  number: (v) => !v || !isNaN(v) || 'يجب أن يكون رقم',
  
  // Positive number validation
  positiveNumber: (v) => !v || (parseFloat(v) > 0) || 'يجب أن يكون رقم موجب',
  
  // File size validation (in bytes)
  fileSize: (maxSize, unit = 'MB') => (file) => {
    if (!file) return true;
    return file.size <= maxSize || `حجم الملف يجب أن يكون أقل من ${maxSize / (1024 * 1024)} ${unit}`;
  },
  
  // File type validation
  fileType: (allowedTypes) => (file) => {
    if (!file) return true;
    return allowedTypes.includes(file.type) || `نوع الملف غير مدعوم. الأنواع المسموحة: ${allowedTypes.join(', ')}`;
  }
};

// File validation utilities
export const fileValidation = {
  // Validate image file with dimensions
  async validateImage(file, options = {}) {
    const {
      maxWidth = 150,
      maxHeight = 150,
      allowedTypes = ['image/png'],
      maxSize = 2 * 1024 * 1024, // 2MB
      required = false
    } = options;

    // Check if file is required
    if (required && !file) {
      return { isValid: false, error: 'الملف مطلوب' };
    }

    if (!file) {
      return { isValid: true };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      const typesText = allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ');
      return { isValid: false, error: `نوع الملف يجب أن يكون ${typesText} فقط` };
    }

    // Check file size
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      return { isValid: false, error: `حجم الملف يجب أن يكون أقل من ${sizeMB} ميجابايت` };
    }

    // Check image dimensions
    try {
      const dimensions = await getImageDimensions(file);
      if (dimensions.width > maxWidth || dimensions.height > maxHeight) {
        return {
          isValid: false,
          error: `أبعاد الصورة ${dimensions.width}x${dimensions.height} تتجاوز الحد المسموح ${maxWidth}x${maxHeight} بكسل`
        };
      }

      return {
        isValid: true,
        dimensions,
        message: `تم تحميل الملف بنجاح - أبعاد الصورة: ${dimensions.width}x${dimensions.height}`
      };
    } catch (error) {
      return { isValid: false, error: 'ملف الصورة تالف أو غير صالح' };
    }
  },

  // Validate stamp file specifically
  async validateStamp(file, required = false) {
    return this.validateImage(file, {
      maxWidth: 150,
      maxHeight: 150,
      allowedTypes: ['image/png'],
      maxSize: 2 * 1024 * 1024,
      required
    });
  },

  // Validate profile picture
  async validateProfilePicture(file, required = false) {
    return this.validateImage(file, {
      maxWidth: 300,
      maxHeight: 300,
      allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'],
      maxSize: 5 * 1024 * 1024, // 5MB
      required
    });
  },

  // Validate document file
  validateDocument(file, options = {}) {
    const {
      allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      maxSize = 10 * 1024 * 1024, // 10MB
      required = false
    } = options;

    if (required && !file) {
      return { isValid: false, error: 'الملف مطلوب' };
    }

    if (!file) {
      return { isValid: true };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'نوع الملف غير مدعوم. يُقبل PDF و Word فقط' };
    }

    // Check file size
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      return { isValid: false, error: `حجم الملف يجب أن يكون أقل من ${sizeMB} ميجابايت` };
    }

    return { isValid: true, message: 'تم تحميل الملف بنجاح' };
  }
};

// Helper function to get image dimensions
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const imageUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Invalid image file'));
    };

    img.src = imageUrl;
  });
}

// Form validation helper
export const formValidation = {
  // Validate entire form object
  validateForm(formData, rules) {
    const errors = {};
    let isValid = true;

    for (const [field, fieldRules] of Object.entries(rules)) {
      const value = formData[field];
      
      for (const rule of fieldRules) {
        const result = rule(value);
        if (result !== true) {
          errors[field] = result;
          isValid = false;
          break; // Stop at first error for this field
        }
      }
    }

    return { isValid, errors };
  },

  // Get validation rules for common form types
  getUserFormRules() {
    return {
      name: [validationRules.required('الاسم')],
      username: [validationRules.required('اسم المستخدم'), validationRules.username],
      email: [validationRules.required('البريد الإلكتروني'), validationRules.email],
      phone: [validationRules.phone],
      emp_no: [validationRules.empNo],
      nid: [validationRules.nationalId]
    };
  },

  getDepartmentFormRules() {
    return {
      rrn: [validationRules.required('الرقم الإشاري')],
      name: [validationRules.required('اسم القسم')]
    };
  },

  getRoleFormRules() {
    return {
      name: [validationRules.required('اسم الدور')],
      description: [validationRules.required('الوصف')]
    };
  },

  getMailPriorityFormRules() {
    return {
      name: [validationRules.required('اسم الأولوية')],
      desc: [validationRules.required('الوصف')],
      level: [validationRules.required('المستوى'), validationRules.positiveNumber]
    };
  }
};

export default {
  validationRules,
  fileValidation,
  formValidation
}; 